import React from 'react';

/**
 * Allocation breakdown card showing detailed allocation calculations
 *
 * @param {Object} props
 * @param {string} props.title - Card title (e.g., "Wholesale Allocation")
 * @param {string} props.titleColor - Color class for title (e.g., 'text-blue-600', 'text-red-600')
 * @param {number} [props.initialAllocation=0] - Starting allocation
 * @param {number} [props.confirmedOrders=0] - Confirmed orders impact
 * @param {number} [props.packed=0] - Packed items impact
 * @param {number} [props.adjustments=0] - Adjustments
 * @param {number} [props.receipts=0] - Receipts
 * @param {number} [props.transfers=0] - Transfers
 * @param {number} [props.inCart=0] - In cart (for First Party only)
 * @param {number} [props.remainingAvailable=0] - Remaining available quantity
 * @param {boolean} [props.showInCartAmount=false] - Whether to show in cart column
 * @param {boolean} [props.showSoftCommittedAmount=false] - Whether to show soft committed amount (displays absolute value of inCart + confirmedOrders)
 */
function AllocationBreakdownCard({
    adjustments = 0,
    confirmedOrders = 0,
    inCart = 0,
    initialAllocation = 0,
    packed = 0,
    receipts = 0,
    remainingAvailable = 0,
    showInCartAmount = false,
    showSoftCommittedAmount = false,
    title,
    titleColor,
    transfers = 0,
}) {
    const formatNumber = (num) => {
        const formatted = Math.abs(num).toLocaleString();
        if (num < 0) return `-${formatted}`;
        if (num > 0) return `+${formatted}`;
        return formatted;
    };

    const getNumberColor = (num) => {
        if (num > 0) return 'text-green-600';
        if (num < 0) return 'text-red-600';
        return 'text-gray-900';
    };

    const softCommitted = Math.abs(inCart + confirmedOrders);

    return (
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            {/* Title */}
            <h3 className={`text-lg font-semibold ${titleColor} mb-4 text-left`}>{title}</h3>

            {/* Allocated Quantity */}
            <div className="mb-4">
                <div className="flex justify-between items-baseline mb-1">
                    <span className="text-sm font-medium text-gray-700">Allocated Quantity</span>
                    <span className={`text-xl font-bold ${titleColor} tabular-nums`}>
                        {initialAllocation.toLocaleString()}
                    </span>
                </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 my-4" />

            {/* Breakdown Items */}
            <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 pl-4">Initial Allocation</span>
                    <span className="text-sm font-medium text-gray-900 tabular-nums">
                        {initialAllocation.toLocaleString()}
                    </span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 pl-4">
                        Confirmed Orders {showInCartAmount ? '/ In Cart' : ''}
                    </span>
                    <span>
                        <span className={`text-sm font-medium ${getNumberColor(confirmedOrders)} tabular-nums`}>
                            {formatNumber(confirmedOrders)}
                        </span>
                        {showInCartAmount && (
                            <>
                                <span className="mx-2"> / </span>
                                <span className={`text-sm font-medium ${getNumberColor(inCart)} tabular-nums`}>
                                    {formatNumber(inCart)}
                                </span>
                            </>
                        )}
                    </span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 pl-4">Packed</span>
                    <span className={`text-sm font-medium ${getNumberColor(packed)} tabular-nums`}>
                        {formatNumber(packed)}
                    </span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 pl-4">Transfers / Adjustments</span>
                    <span>
                        <span className={`text-sm font-medium ${getNumberColor(transfers)} tabular-nums`}>
                            {formatNumber(transfers)}
                        </span>
                        <span className="mx-2"> / </span>
                        <span className={`text-sm font-medium ${getNumberColor(adjustments)} tabular-nums`}>
                            {formatNumber(adjustments)}
                        </span>
                    </span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 pl-4">Receipts</span>
                    <span className={`text-sm font-medium ${getNumberColor(receipts)} tabular-nums`}>
                        {formatNumber(receipts)}
                    </span>
                </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 border-2 my-4" />

            {/* Remaining Available */}
            <div className="flex justify-between items-baseline">
                <span className="text-base font-semibold text-gray-900">Remaining Available</span>
                <div className="flex items-baseline gap-2">
                    <span
                        className={`text-xl font-bold tabular-nums ${remainingAvailable < 0 ? 'text-red-600' : 'text-green-600'}`}
                    >
                        {remainingAvailable.toLocaleString()}
                    </span>
                    {showSoftCommittedAmount && softCommitted > 0 && (
                        <span className="text-sm text-gray-500 tabular-nums">
                            (+{softCommitted.toLocaleString()} soft)
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AllocationBreakdownCard;
